import React, { FC, Fragment, useState, useEffect } from 'react';
import { Box, List, ListItem, ListItemText, ListItemAvatar, Avatar, Typography, IconButton } from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDropBoxArea from 'components/shared/file-drop-box-area/file-drop-box-area.component';
import FileUploadedTable from 'components/shared/file-uploaded-table/file-uploaded-table.component';
import { File as DenthubFile } from 'models/file';
import { Order } from 'models/order';
import { useTranslation } from 'react-i18next';
import ImageGalleryDialog from 'components/shared/image-gallery-dialog/image-gallery-dialog.component';
import InfoText from '../info-text.component';

type FilesImagesUploadAreaAndTableProps = {
    files: DenthubFile[];
    onDeleteFile: (fileId: string) => void;
    onFileChange: (files: File[]) => void;
    onEditFile?: (file: DenthubFile) => void;
    versionCheck?: boolean;
    showHightLight?: boolean;
    originalOrder?: Order;
    showAsSimpleList?: boolean;
    handleRemoveFile?: (index: number) => void;
};
const FilesImagesUploadAreaAndTable: FC<FilesImagesUploadAreaAndTableProps> = ({
    files,
    onDeleteFile,
    onFileChange,
    onEditFile,
    versionCheck,
    showHightLight,
    originalOrder,
    showAsSimpleList,
    handleRemoveFile,
}: FilesImagesUploadAreaAndTableProps) => {
    const { t: translate } = useTranslation(['orders', 'common']);
    const [currentFiles, setCurrentFiles] = useState<DenthubFile[]>([]);
    const [allImages, setAllImages] = useState<string[]>([]);
    const [showImageGallery, setShowImageGallery] = useState(false);
    const [selectedImage, setSelectedImage] = useState<string>();

    useEffect(() => {
        setCurrentFiles(files);
        prepareImages();
    }, [files]);

    const prepareImages = () => {
        const images: string[] = [];
        files.map((file) => {
            if (/image/.test(file.mimetype)) {
                images.push(file.url);
            }
        });
        setAllImages(images);
    };

    const handleImageClick = (imageUrl: string) => {
        setSelectedImage(imageUrl);
        setShowImageGallery(true);
    };

    const removeFile = (index: number) => {
        if (handleRemoveFile) {
            handleRemoveFile(index);
        }
    };

    return (
        <Fragment>
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                {!showAsSimpleList && currentFiles.length > 0 ? (
                    <FileUploadedTable
                        versionCheck={versionCheck}
                        onDeleteFile={onDeleteFile}
                        onImageClick={handleImageClick}
                        onEditFile={onEditFile}
                        files={currentFiles}
                        showHightLight={showHightLight}
                        originalOrder={originalOrder}
                    />
                ) : showAsSimpleList && currentFiles.length > 0 ? (
                    <Fragment>
                        <Typography variant="h3">{translate('common:FILES')}</Typography>
                        <List dense>
                            {currentFiles.map((file: any, index: number) => (
                                <ListItem
                                    key={file.path}
                                    data-test={`files-images-upload-area-and-table.file-item-${index}`}
                                    secondaryAction={
                                        <IconButton
                                            onClick={() => removeFile(index)}
                                            edge="end"
                                            aria-label="delete"
                                            data-test={`files-images-upload-area-and-table.remove-file-button-${index}`}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    }
                                >
                                    <ListItemAvatar>
                                        <Avatar>
                                            <InsertDriveFileIcon />
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText primary={file.path} />
                                </ListItem>
                            ))}
                        </List>
                    </Fragment>
                ) : (
                    <Box mb={1}>
                        <InfoText text={translate('orders:NO_FILES_ATTACHED')} />
                    </Box>
                )}
                {versionCheck || versionCheck === undefined ? (
                    <Box mb={1}>
                        <FileDropBoxArea handleChange={onFileChange} />
                    </Box>
                ) : undefined}
            </Box>

            <ImageGalleryDialog
                imageUrls={allImages}
                selectedImageUrl={selectedImage}
                open={showImageGallery}
                onClose={() => {
                    setShowImageGallery(false);
                }}
            />
        </Fragment>
    );
};

export default FilesImagesUploadAreaAndTable;
